import { writable, derived, get } from 'svelte/store';
import { COINBASE, COINBASE_SANDBOX } from './venues.js';

// The app-wide trading context (ADR-0011 AC 8/9; renamed + widened by ADR-0012 D8/D9; venue-only since
// ADR-0040).
//
// The header toggle selects Sandbox|Live — a VENUE context: switching re-points EVERY venue-scoped call
// (market data, orders, positions, autotrader, portfolio) and the SSE stream, and drives the app-wide
// salience treatment (sandbox theme + FAKE bill + [SANDBOX] title) from this ONE store — the theme can
// never disagree with the venue being called.
//
//   Sandbox (venue 6, Coinbase Exchange sandbox): execution-mechanics testing — real API, fake funds.
//   Live    (venue 2, Coinbase Advanced):         real money — AND hosts the Paper Trading view, which
//           since ADR-0040 is the PAPER RUN MODE on this same venue (simulated fills against Coinbase's
//           live book), not a venue of its own. The run TARGET {venue, mode} a view works with is
//           derived from this context by lib/runContext.js (`targetFor`); this store stays venue-only.
//
// Persistence: per user+org in localStorage; first-ever default = Sandbox (safe-by-default). The
// sandbox context requires the org's CBX credential — without it the toggle is disabled (venue 6
// would fail fast on every signed call) and the context is forced to Live. Legacy persisted
// 'paper' values (the pre-rename context name) migrate to 'sandbox'.

/** 'sandbox' | 'live' — the current trading context. Drive changes through setTradingContext only. */
export const tradingContext = writable('sandbox');

/** Whether the current org has a sandbox (CBX) credential — gates the toggle + the sandbox default. */
export const sandboxCredsPresent = writable(false);

/** The venue id every venue-scoped call must use (runContext.js derives the run target from this). */
export const activeExchangeId = derived(tradingContext, (ctx) => exchangeIdFor(ctx));

/** True in the sandbox context — the theme/bill/title salience flag (AC 9). */
export const isSandboxContext = derived(tradingContext, (ctx) => ctx === 'sandbox');

/** Backtest is a Live-context tool (ADR-0012 AC 2): hidden while the Sandbox context is active. */
export const showBacktest = derived(tradingContext, (ctx) => ctx === 'live');

/** The Paper Trading view (Paper mode on Coinbase) lives inside the Live context only (ADR-0012 AC 3). */
export const showPaperTrading = derived(tradingContext, (ctx) => ctx === 'live');

export function exchangeIdFor(context) {
  return context === 'live' ? COINBASE : COINBASE_SANDBOX;
}

/**
 * The SSE venue scope for a context — a venue SET in the wire form the server parses (ADR-0012 D9).
 * Since ADR-0040 the Live context is venue 2 alone: Paper and Live runs on Coinbase share the venue
 * and differ only by the event's `mode` (the optional `?mode=` stream filter — runContext.js).
 */
export function venueScopeFor(context) {
  return [exchangeIdFor(context)];
}

export function storageKey(userId, orgId) {
  return `k2.tradingContext.${userId}.${orgId}`;
}

function normalize(raw) {
  if (raw === 'paper') return 'sandbox'; // pre-rename persisted value (ADR-0011 era) — migrate, don't reset
  return raw === 'live' || raw === 'sandbox' ? raw : null;
}

// The persistence key for the CURRENT user+org (set by syncVenueContext; setTradingContext writes to it).
let currentKey = null;

/**
 * (Re)resolve the context for a user+org — call on sign-in and on every org switch. The stored
 * preference wins when valid; first-ever default is 'sandbox' (safe-by-default). Either way,
 * 'sandbox' requires the CBX credential — absent it, the context is forced 'live' (the toggle
 * renders disabled with the Vault hint). Returns the resolved context.
 *
 * `legacyUserId` (TASK-20260818 F7): PR #61 changed the key identity from the email to the Clerk
 * user id — correct (an email is not a tenant key), but it ORPHANED every preference persisted
 * under the email key: the stored 'live' became unreadable, the sandbox default kicked in, and the
 * whole app silently re-pointed at venue 6 (sandbox balances rendered as "the portfolio", sandbox
 * order history as "the orders", sandbox timeouts as 500s). When the id key has no value, a valid
 * preference under the legacy email key is adopted, re-persisted under the id key, and the legacy
 * key removed — an identity migration must migrate the state keyed on it.
 */
export function syncVenueContext(userId, orgId, hasSandboxCreds, storage = globalThis.localStorage, legacyUserId = null) {
  sandboxCredsPresent.set(!!hasSandboxCreds);
  currentKey = storageKey(userId, orgId);

  let stored = null;
  try {
    stored = normalize(storage?.getItem(currentKey));

    if (stored === null && legacyUserId && legacyUserId !== userId) {
      const legacyKey = storageKey(legacyUserId, orgId);
      const legacy = normalize(storage?.getItem(legacyKey));
      if (legacy !== null) {
        stored = legacy;
        storage?.setItem(currentKey, legacy); // migrate forward (normalized — 'paper' lands as 'sandbox')
        storage?.removeItem(legacyKey);       // one-time: the legacy key never wins again
      }
    }
  } catch {
    // Storage unavailable (private mode etc.) — fall through to the default.
  }

  let ctx = stored ?? 'sandbox';
  if (ctx === 'sandbox' && !hasSandboxCreds) {
    ctx = 'live';
  }

  tradingContext.set(ctx);
  return ctx;
}

/**
 * Switch context (the header toggle). Ignores junk values; refuses 'sandbox' while the org has no
 * CBX credential (the UI disables the toggle — this is the store-level backstop). Persists the
 * choice under the current user+org key.
 */
export function setTradingContext(context, storage = globalThis.localStorage) {
  const ctx = normalize(context);
  if (!ctx) return;
  if (ctx === 'sandbox' && !get(sandboxCredsPresent)) return;

  tradingContext.set(ctx);
  if (currentKey) {
    try {
      storage?.setItem(currentKey, ctx);
    } catch {
      // Best-effort persistence; the in-memory context still switched.
    }
  }
}
